import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import { expenseCaptureApi } from '../services/expenseCaptureApi';
import type { ExpenseCategory, ExpenseCompany } from '../types/expense';
import { ExpenseManagerView } from '../components/expense/ExpenseManagerView';
import { ExpenseDetail } from '../components/expense/ExpenseDetail';

/**
 * Expense Management — desktop country-manager review page (separate from the mobile
 * capture PWA). Access is governed by the normal Settings permission matrix on
 * /expense-management; the API scopes the data (admin = all, else the manager's
 * companies). Reuses the manager list + detail components and the existing endpoints.
 * Phase 2 (card reconciliation) will extend this page.
 */
export function ExpenseManagementPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { canView, isLoading: permLoading } = usePagePermissions('/expense-management');
    const { toast } = useToast();

    const [companies, setCompanies] = useState<ExpenseCompany[]>([]);
    const [categories, setCategories] = useState<ExpenseCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (!canView) return;
        let active = true;
        Promise.all([expenseCaptureApi.listCompanies(), expenseCaptureApi.listCategories()])
            .then(([comps, cats]) => {
                if (!active) return;
                setCompanies(comps.companies);
                setCategories(cats.categories);
            })
            .catch(() => { if (active) toast('Could not load expense settings.', 'error'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [canView, toast]);

    if (permLoading) return <div className="p-6 text-gray-400">Loading …</div>;
    if (!canView) return <div className="p-6 text-red-500">You do not have access to Expense Management.</div>;

    return (
        <div className="mx-auto max-w-6xl space-y-5">
            <div className="flex items-center gap-2">
                <Wallet className="h-6 w-6 text-blue-600" />
                <div>
                    <h1 className="text-xl font-bold text-gray-900">Expense Management</h1>
                    <p className="text-xs text-gray-500">Review submitted expense receipts for your country.</p>
                </div>
            </div>

            {selectedId ? (
                <ExpenseDetail
                    receiptId={selectedId}
                    currentUserEmail={user?.email ?? ''}
                    categories={categories}
                    onBack={() => setSelectedId(null)}
                    onResume={() => navigate('/expense-capture')}
                    onChanged={() => setRefreshKey(k => k + 1)}
                />
            ) : loading ? (
                <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading…
                </div>
            ) : (
                <ExpenseManagerView key={refreshKey} companies={companies} onSelect={(r) => setSelectedId(r.Id)} />
            )}
        </div>
    );
}
